import type { FC } from 'react';
import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
import { css, styled as styled2 } from '@compiled/react';

import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { durations } from '@atlaskit/motion';
import { layers } from '@atlaskit/theme/constants';

import { AdminHomeOnboardingTakeoverV2 } from '@confluence/experiment-home-onboarding-takeover-v2/entry-points/AdminHomeOnboardingTakeoverV2';
import { useWebPanelLocation } from '@confluence/web-panel-location/entry-points/useWebPanelLocation';
import {
	FEED_EXPERIENCE,
	HOME_OVERVIEW_RECENT_EXPERIENCE,
	HOME_OVERVIEW_EXPERIENCE,
	HOME_RIGHT_PANEL_APPS_EXPERIENCE,
	ExperienceStart,
	ExperienceSuccess,
	ExperienceTimeout,
	collectAll,
} from '@confluence/experience-tracker';
import { WebPanel } from '@confluence/web-panel-location';
import { PageSegmentLoadStart } from '@confluence/browser-metrics';
import { Feed, HOME_OVERVIEW_FEED_PAGE_SEGMENT } from '@confluence/feed';
import type { CustomPanel } from '@confluence/feed';
import { DASHBOARD_SECONDARY } from '@confluence/web-item-location';
import { useTemplatesCollectionEligible } from '@confluence/onboarding-helpers/entry-points/hooks/useTemplatesCollectionEligible';
import { DocumentContentTitle } from '@confluence/document-title';
import { PostOfficeHomeBanner } from '@confluence/experiment-post-office-home-banner/entry-points/PostOfficeHomeBanner';
import { usePostOfficeHomeBannerEligible } from '@confluence/experiment-post-office-home-banner/entry-points/usePostOfficeHomeBannerExperiment';
import { LazyAutomationExecutionLimit } from '@confluence/automation/entry-points/execution-limit';
import { fg } from '@confluence/feature-gating';

import {
	HOME_OVERVIEW_RECENT_PAGE_SEGMENT,
	HOME_OVERVIEW_RIGHT_PANEL_PAGE_SEGMENT,
} from '../../../perf.config';
import { RightPanel } from '../../RightPanel/RightPanel';
import {
	OnboardingTakeoverNewHomeExperienceLoader,
	useOnboardingTakeoverType,
} from '../../Onboarding';
import { useRightPanelVisible } from '../../RightPanel/useRightPanelVisible';
import { HomeWelcomeMessage } from '../../RightPanel/WelcomeMessage/LazyWelcomeMessage';
import { Calendars } from '../../RightPanel/Calendars';
import {
	LazyFeedbackIcon as FeedbackIcon,
	LazyCalendarIcon as CalendarIcon,
} from '../images/lazyImages';

import { RecentList } from './RecentList';
import { TemplateCollectionsLoader } from './CreateFromTemplate';

const i18n = defineMessages({
	homeTitle: {
		id: 'home.title',
		defaultMessage: 'Home',
		description: 'Title for the home page',
	},
	recentWork: {
		id: 'home.views.overview.recent-work',
		defaultMessage: 'Pick up where you left off',
		description: 'Header for a list of users recently visited pages',
	},
	calendarTitle: {
		id: 'home.feed.calendar.title',
		defaultMessage: 'Calendars',
		description: "Title for home's calendar tab",
	},
	announcementsTitle: {
		id: 'home.feed.announcements.title',
		defaultMessage: 'Announcements',
		description: "Title for home's annuncements tab",
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const OverviewContainer = styled.div<{
	isOnboardingExperienceEligible: boolean;
	isTemplatesCollectionEligible: boolean;
	isPostOfficeHomeBannerEligible: boolean;
	shouldShowRightPanel?: boolean;
}>`
	width: 100%;
	display: grid;
	grid-column-gap: ${token('space.400', '32px')};
	grid-template-rows: auto auto 1fr;
	/* Add an additional row for PostOfficeHomeBanner */
	${({ isPostOfficeHomeBannerEligible }) =>
		`${
			isPostOfficeHomeBannerEligible
				? 'grid-template-rows: auto auto auto 1fr;'
				: 'grid-template-rows: auto auto 1fr;'
		}`}
	grid-template-columns: 100% 0;
	${({ shouldShowRightPanel }) =>
		shouldShowRightPanel &&
		`
  /* If --homeIsSingleColumn == 0 then include a 300px sidebar */
  grid-template-columns:
    calc((100% - calc((1 - var(--homeIsSingleColumn)) * ${token(
			'space.400',
			'32px',
		)}) - (1 - var(--homeIsSingleColumn)) * 300px))
    calc((1 - var(--homeIsSingleColumn)) * 300px);
    transition: ${durations.medium}ms;
  `}

	${(props) =>
		props.isOnboardingExperienceEligible
			? '--homeHasOnboardingExperience: 1;'
			: '--homeHasOnboardingExperience: 0;'}

  	${(props) =>
		props.isTemplatesCollectionEligible
			? '--homeShowTemplateCollections: 1;'
			: '--homeShowTemplateCollections: 0;'}

	${(props) =>
		props.isPostOfficeHomeBannerEligible
			? '--homeShowPostOfficeBanner: 1;'
			: '--homeShowPostOfficeBanner: 0;'}
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const OverviewContainer2 = styled2.div<{
	isOnboardingExperienceEligible: boolean;
	isTemplatesCollectionEligible: boolean;
	isPostOfficeHomeBannerEligible: boolean;
	shouldShowRightPanel?: boolean;
}>({
	width: '100%',
	display: 'grid',
	gridColumnGap: token('space.400', '32px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridTemplateRows: ({ isPostOfficeHomeBannerEligible }) =>
		isPostOfficeHomeBannerEligible ? 'auto auto auto 1fr' : 'auto auto 1fr',
	/* If --homeIsSingleColumn == 0 then include a 300px sidebar */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridTemplateColumns: ({ shouldShowRightPanel }) =>
		shouldShowRightPanel
			? `calc((100% - calc((1 - var(--homeIsSingleColumn)) * ${token('space.400', '32px')}) - (1 - var(--homeIsSingleColumn)) * 300px)) calc((1 - var(--homeIsSingleColumn)) * 300px)`
			: '100% 0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	transition: ({ shouldShowRightPanel }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		shouldShowRightPanel ? `${durations.medium}ms` : 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	'--home-has-onboarding-experience': ({ isOnboardingExperienceEligible }) =>
		isOnboardingExperienceEligible ? '1' : '0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	'--home-show-template-collections': ({ isTemplatesCollectionEligible }) =>
		isTemplatesCollectionEligible ? '1' : '0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	'--home-show-post-office-banner': ({ isPostOfficeHomeBannerEligible }) =>
		isPostOfficeHomeBannerEligible ? '1' : '0',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const OnboardingExperienceContainer = styled.div`
	grid-column: 1 / span 1;
	grid-row: 1 / span 1;
	position: relative;
	margin-bottom: calc(
		(32px - 32px * var(--homeIsSingleColumn)) + (12px * var(--homeIsSingleColumn))
	);
`;

const onboardingExperienceContainerStyles = css({
	gridColumn: '1 / span 1',
	gridRow: '1 / span 1',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginBottom:
		'calc((32px - 32px * var(--homeIsSingleColumn)) + (12px * var(--homeIsSingleColumn)))',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const TemplatesContainer = styled.div`
	position: relative;
	grid-column: 1 / span 1;
	/* If --homeHasOnboardingExperience == 1 then row = 2 else row = 1 */
	grid-row: (1 + var(--homeHasOnboardingExperience)) / span 1;
	margin-top: 0px;
	margin-bottom: ${token('space.300', '24px')};
`;

const templatesContainerStyles = css({
	position: 'relative',
	gridColumn: '1 / span 1',
	// If --home-has-onboarding-experience == 1 then row = 2 else row = 1
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridRow: '(1 + var(--home-has-onboarding-experience)) / span 1',
	marginTop: '0px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginBottom: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const RecentContainer = styled.div`
	position: relative;
	grid-column: 1 / span 1;
	/* If --homeHasOnboardingExperience == 1 then row = 2 else row = 1 */
	grid-row: (1 + var(--homeHasOnboardingExperience) + var(--homeShowTemplateCollections)) / span 1;
	margin-top: 0px;
	margin-bottom: ${token('space.300', '24px')};
`;

const recentContainerStyles = css({
	position: 'relative',
	gridColumn: '1 / span 1',
	// If --home-has-onboarding-experience == 1 then row = 2 else row = 1
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridRow:
		'(1 + var(--home-has-onboarding-experience) + var(--home-show-template-collections)) / span 1',
	marginTop: '0px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	marginBottom: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const BannerContainer = styled.div`
	grid-column: 1 / span 1;
	/* If --homeIsSingleColumn == 1 then row = 3 else row = 2 */
	/* Above Condition and If --homeHasOnboardingExperience == 1 then row = 4 else row = 3 */
	grid-row: calc(
			2 + var(--homeIsSingleColumn) + var(--homeHasOnboardingExperience) +
				var(--homeShowTemplateCollections)
		) / span 1;
`;

const bannerContainerStyles = css({
	gridColumn: '1 / span 1',
	// If --homeIsSingleColumn == 1 then row = 3 else row = 2
	// Above Condition and If --home-has-onboarding-experience == 1 then row = 4 else row = 3
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridRow: `calc(
			2 + var(--homeIsSingleColumn) + var(--home-has-onboarding-experience) +
				var(--home-show-template-collections)
		) / span 1`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const FeedContainer = styled.div`
	grid-column: 1 / span 1;
	/* If --homeIsSingleColumn == 1 then row = 3 else row = 2 */
	/* Above Condition and If --homeHasOnboardingExperience == 1 then row = 4 else row = 3 */
	/* Above Condition and If --homeShowPostOfficeBanner == 1 then row = 5 else row = 4 */
	grid-row: calc(
			2 + var(--homeIsSingleColumn) + var(--homeHasOnboardingExperience) +
				var(--homeShowTemplateCollections) + var(--homeShowPostOfficeBanner)
		) / span 1;
`;

const feedContainerStyles = css({
	gridColumn: '1 / span 1',
	// If --homeIsSingleColumn == 1 then row = 3 else row = 2
	// Above Condition and If --home-has-onboarding-experience == 1 then row = 4 else row = 3
	// Above Condition and If --home-show-post-office-banner == 1 then row = 5 else row = 4
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridRow: `calc(
			2 + var(--homeIsSingleColumn) + var(--home-has-onboarding-experience) +
				var(--home-show-template-collections) + var(--home-show-post-office-banner)
		) / span 1`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const RightPanelContainer = styled.div`
	margin-top: calc(
		34px * (1 - var(--homeIsSingleColumn)) * (1 - var(--homeHasOnboardingExperience))
	);
	transition: margin-top 0.5s;
	/* If --homeIsSingleColumn == 1 then col = 1 else col = 2 */
	grid-column: calc(2 - var(--homeIsSingleColumn)) / span 1;
	/* If --homeIsSingleColumn == 1 then row = 2 / span 1 else row = 1 / span 2 */
	grid-row: calc(
			1 + (1 + var(--homeHasOnboardingExperience) + var(--homeShowTemplateCollections)) *
				var(--homeIsSingleColumn)
		) / span calc(3 - 2 * var(--homeIsSingleColumn));
	min-width: 300px;
`;

const rightPanelContainerStyles = css({
	marginTop:
		'calc(34px * (1 - var(--homeIsSingleColumn)) * (1 - var(--home-has-onboarding-experience)))',
	transition: 'margin-top 0.5s',
	// If --homeIsSingleColumn == 1 then col = 1 else col = 2
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridColumn: `calc(2 - var(--homeIsSingleColumn)) / span 1`,
	// If --homeIsSingleColumn == 1 then row = 2 / span 1 else row = 1 / span 2
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridRow: `calc(
			1 + (1 + var(--home-has-onboarding-experience) + var(--home-show-template-collections)) *
				var(--homeIsSingleColumn)
		) / span calc(3 - 2 * var(--homeIsSingleColumn))`,
	minWidth: '300px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const RecentSpotlightContainer = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
	left: ${token('space.negative.050', '-4px')};
	padding: ${token('space.050', '4px')} ${token('space.050', '4px')} ${token('space.025', '2px')};
	pointer-events: none;
`;

const recentSpotlightContainerStyles = css({
	position: 'absolute',
	width: '100%',
	height: '100%',
	left: token('space.negative.050', '-4px'),
	padding: `${token('space.050', '4px')} ${token('space.050', '4px')} ${token('space.025', '2px')}`,
	pointerEvents: 'none',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const RecentHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: ${token('space.200', '16px')};
`;

const recentHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const LimitContainer = styled.div`
	position: fixed;
	left: ${token('space.1000', '80px')};
	z-index: ${layers.modal() + 50};
	bottom: ${token('space.1000', '80px')};
	box-shadow: ${token(
		'elevation.shadow.overlay',
		'0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 3px 5px 0px rgba(9, 30, 66, 0.20)',
	)};
	border-radius: 4px;
`;

const limitContainerStyles = css({
	position: 'fixed',
	left: token('space.1000', '80px'),
	zIndex: layers.modal() + 50,
	bottom: token('space.1000', '80px'),
	boxShadow: token(
		'elevation.shadow.overlay',
		'0px 0px 1px 0px rgba(9, 30, 66, 0.31), 0px 3px 5px 0px rgba(9, 30, 66, 0.20)',
	),
	borderRadius: '4px',
});

const collectOverview = (events, experience) => {
	const collection = [
		FEED_EXPERIENCE,
		HOME_OVERVIEW_RECENT_EXPERIENCE,
		HOME_RIGHT_PANEL_APPS_EXPERIENCE,
	];
	collectAll(collection)(events, experience);
};

export const Overview: FC = () => {
	const intl = useIntl();

	const onboardingTakeoverType = useOnboardingTakeoverType();
	const { isTemplatesCollectionEligible } = useTemplatesCollectionEligible();
	const { isEligible: isPostOfficeHomeBannerEligible } = usePostOfficeHomeBannerEligible();
	const { shouldShowRightPanel } = useRightPanelVisible();

	const {
		panels,
		loading: appsLoading,
		error: appsError,
	} = useWebPanelLocation({
		//no way to conditionally load this? Because hooks can't be conditional
		location: DASHBOARD_SECONDARY,
		fetchPolicy: 'cache-first',
	});

	const feedCustomWebSections =
		(panels &&
			panels
				.sort(({ weight: i }, { weight: j }) => (i > j ? 1 : -1))
				.map(({ html, completeKey, label }) =>
					completeKey.includes('team-calendars')
						? {
								title: intl.formatMessage(i18n.calendarTitle),
								key: completeKey,
								icon: CalendarIcon,
								content: <Calendars completeKey={completeKey} key={completeKey} noCollapse />,
							}
						: {
								title: label,
								icon: undefined,
								key: completeKey,
								content: <WebPanel html={html} />,
							},
				)) ||
		[];

	const feedCustomPanels: CustomPanel[] = [
		{
			title: intl.formatMessage(i18n.announcementsTitle),
			key: 'confluence.home.announcements',
			// @ts-ignore TODO: fix the CustomPanel type to accept LoadableType icons
			icon: FeedbackIcon,
			content: <HomeWelcomeMessage noCollapse />, //also has onLoadProp for experience tracking?
		},
		// @ts-ignore TODO: fix the CustomPanel type to accept LoadableType icons
		...feedCustomWebSections,
	];

	if (fg('confluence_frontend_home_compiled_migration')) {
		return (
			<>
				<OverviewContainer2
					isOnboardingExperienceEligible={Boolean(onboardingTakeoverType)}
					isTemplatesCollectionEligible={isTemplatesCollectionEligible}
					isPostOfficeHomeBannerEligible={isPostOfficeHomeBannerEligible}
					shouldShowRightPanel={shouldShowRightPanel}
					data-testid="home_overview"
				>
					<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.homeTitle)} />
					<ExperienceStart name={HOME_OVERVIEW_EXPERIENCE} collect={collectOverview} />
					<ExperienceStart
						name={HOME_OVERVIEW_RECENT_EXPERIENCE}
						timeout={ExperienceTimeout.HOME}
					/>
					{onboardingTakeoverType === 'v1' && (
						<div css={onboardingExperienceContainerStyles}>
							<OnboardingTakeoverNewHomeExperienceLoader />
						</div>
					)}
					{(onboardingTakeoverType === 'v2-land' || onboardingTakeoverType === 'v2-xflow') && (
						<div css={onboardingExperienceContainerStyles}>
							<AdminHomeOnboardingTakeoverV2 takeoverType={onboardingTakeoverType} />
						</div>
					)}
					{isTemplatesCollectionEligible &&
						!(onboardingTakeoverType === 'v2-land' || onboardingTakeoverType === 'v2-xflow') && (
							<div css={templatesContainerStyles}>
								<TemplateCollectionsLoader />
							</div>
						)}
					<div css={recentContainerStyles}>
						<PageSegmentLoadStart metric={HOME_OVERVIEW_RECENT_PAGE_SEGMENT} />
						<SpotlightTarget name="home-recent">
							<div css={recentSpotlightContainerStyles} />
						</SpotlightTarget>
						<div css={recentHeaderStyles}>
							<Heading size="xsmall" as="h2">
								<FormattedMessage {...i18n.recentWork} />
							</Heading>
						</div>
						<RecentList />
					</div>
					{isPostOfficeHomeBannerEligible && (
						<div css={bannerContainerStyles}>
							<PostOfficeHomeBanner />
						</div>
					)}
					<div css={feedContainerStyles}>
						<PageSegmentLoadStart metric={HOME_OVERVIEW_FEED_PAGE_SEGMENT} />
						<ExperienceStart name={FEED_EXPERIENCE} />
						<ExperienceStart name={HOME_RIGHT_PANEL_APPS_EXPERIENCE} />
						<Feed renderPopularFeed customPanels={feedCustomPanels} />
						{!appsLoading && !appsError ? (
							<ExperienceSuccess name={HOME_RIGHT_PANEL_APPS_EXPERIENCE} />
						) : null}
					</div>
					{shouldShowRightPanel && (
						<div css={rightPanelContainerStyles}>
							<PageSegmentLoadStart metric={HOME_OVERVIEW_RIGHT_PANEL_PAGE_SEGMENT} />
							<RightPanel widthMode="fullwidth" />
						</div>
					)}
				</OverviewContainer2>
				{fg('a4c_free_and_standard_onboarding') && (
					<div css={limitContainerStyles}>
						<LazyAutomationExecutionLimit />
					</div>
				)}
			</>
		);
	}

	return (
		<>
			<OverviewContainer
				isOnboardingExperienceEligible={Boolean(onboardingTakeoverType)}
				isTemplatesCollectionEligible={isTemplatesCollectionEligible}
				isPostOfficeHomeBannerEligible={isPostOfficeHomeBannerEligible}
				shouldShowRightPanel={shouldShowRightPanel}
				data-testid="home_overview"
			>
				<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.homeTitle)} />
				<ExperienceStart name={HOME_OVERVIEW_EXPERIENCE} collect={collectOverview} />
				<ExperienceStart name={HOME_OVERVIEW_RECENT_EXPERIENCE} timeout={ExperienceTimeout.HOME} />
				{onboardingTakeoverType === 'v1' && (
					<OnboardingExperienceContainer>
						<OnboardingTakeoverNewHomeExperienceLoader />
					</OnboardingExperienceContainer>
				)}
				{(onboardingTakeoverType === 'v2-land' || onboardingTakeoverType === 'v2-xflow') && (
					<OnboardingExperienceContainer>
						<AdminHomeOnboardingTakeoverV2 takeoverType={onboardingTakeoverType} />
					</OnboardingExperienceContainer>
				)}
				{isTemplatesCollectionEligible &&
					!(onboardingTakeoverType === 'v2-land' || onboardingTakeoverType === 'v2-xflow') && (
						<TemplatesContainer>
							<TemplateCollectionsLoader />
						</TemplatesContainer>
					)}
				<RecentContainer>
					<PageSegmentLoadStart metric={HOME_OVERVIEW_RECENT_PAGE_SEGMENT} />
					<SpotlightTarget name="home-recent">
						<RecentSpotlightContainer />
					</SpotlightTarget>
					<RecentHeader>
						<Heading size="xsmall" as="h2">
							<FormattedMessage {...i18n.recentWork} />
						</Heading>
					</RecentHeader>
					<RecentList />
				</RecentContainer>
				{isPostOfficeHomeBannerEligible && (
					<BannerContainer>
						<PostOfficeHomeBanner />
					</BannerContainer>
				)}
				<FeedContainer>
					<PageSegmentLoadStart metric={HOME_OVERVIEW_FEED_PAGE_SEGMENT} />
					<ExperienceStart name={FEED_EXPERIENCE} />
					<ExperienceStart name={HOME_RIGHT_PANEL_APPS_EXPERIENCE} />
					<Feed renderPopularFeed customPanels={feedCustomPanels} />
					{!appsLoading && !appsError ? (
						<ExperienceSuccess name={HOME_RIGHT_PANEL_APPS_EXPERIENCE} />
					) : null}
				</FeedContainer>
				{shouldShowRightPanel && (
					<RightPanelContainer>
						<PageSegmentLoadStart metric={HOME_OVERVIEW_RIGHT_PANEL_PAGE_SEGMENT} />
						<RightPanel widthMode="fullwidth" />
					</RightPanelContainer>
				)}
			</OverviewContainer>
			{fg('a4c_free_and_standard_onboarding') && (
				<LimitContainer>
					<LazyAutomationExecutionLimit />
				</LimitContainer>
			)}
		</>
	);
};
